import isoCatalog from './data/isoMic.json';
import type { SymbolMatch } from '@biasafe.models';

interface MicRecord {
  country: string;
  domain: string;
  label: string;
  name: string;
  operatingMic: string;
}
const MIC = isoCatalog as Partial<Record<string, MicRecord>>;
// IBKR routes use broker codes as well as ISO MICs. Resolve those aliases to
// their listing/operator MIC; never derive a country from the issuer's name.
const IBKR_MIC: Partial<Record<string, string>> = {
  WSE: 'XWAR',
  VSE: 'XWBO',
  OSE: 'XOSL',
  NASDAQ: 'XNAS',
  NYSE: 'XNYS',
  AMEX: 'XASE',
  ARCA: 'ARCX',
  NYSEARCA: 'ARCX',
  ISLAND: 'XNAS',
  IEX: 'IEXG',
  CBOE: 'XCBO',
  BATS: 'BATS',
  OTCBB: 'OTCM',
  PINK: 'PINX',
  MEXI: 'XMEX',
  EBS: 'XSWX',
  VIRTX: 'XVTX',
  TSE: 'XTSE',
  TSX: 'XTSE',
  VENTURE: 'XTSX',
  TSEVENTURE: 'XTSX',
  CSE: 'XCNQ',
  LSE: 'XLON',
  LSEETF: 'XLON',
  LSEIOB1: 'XLON',
  IBIS: 'XETR',
  IBIS2: 'XETR',
  XETRA: 'XETR',
  FWB: 'XFRA',
  FWB2: 'XFRA',
  STUTTGART: 'XSTU',
  GETTEX: 'MUNC',
  TRADEGATE: 'XGAT',
  SBF: 'XPAR',
  AEB: 'XAMS',
  BVME: 'XMIL',
  BVMEETF: 'XMIL',
  BM: 'XMAD',
  BMEX: 'BMEX',
  BELFOX: 'XBRU',
  ENEXTBE: 'XBRU',
  FWBGETTEX: 'MUNC',
  ASX: 'XASX',
  TASE: 'XTAE',
  TSEJ: 'XTKS',
  SEHK: 'XHKG',
  SSE: 'XSHG',
  SZSE: 'XSHE',
  SGX: 'XSES',
  SGXST: 'XSES',
  KSE: 'XKRX',
  NSE: 'XNSE',
  BSE: 'XBOM',
  JSE: 'XJSE',
};

export function instrumentExchange(match: SymbolMatch): string {
  const explicit = match.description?.trim().toUpperCase();
  if (explicit) return explicit;
  const suffix =
    match.name?.match(/\(([A-Z][A-Z0-9._-]{1,24})\)\s*$/)?.[1] ?? '';
  return IBKR_MIC[suffix] || MIC[suffix] || suffix === 'FUNDSERV' ? suffix : '';
}
/** Hide only the redundant trailing venue; preserve issuer qualifiers. */
export function instrumentDisplayName(match: SymbolMatch): string {
  const name = (match.name ?? '').trim();
  const code = instrumentExchange(match);
  const suffix = name.match(/\s*\(([^()]*)\)\s*$/);
  return code && suffix?.[1].trim().toUpperCase() === code
    ? name.slice(0, suffix.index).trim()
    : name;
}

export function instrumentVenue(match: SymbolMatch) {
  const code = instrumentExchange(match);
  const mic = IBKR_MIC[code] ?? code;
  const record = MIC[mic];
  if (!record) return undefined;
  const operator = MIC[record.operatingMic];
  return {
    mic,
    country: record.country,
    label: code === 'TSE' ? 'TSX' : code,
    domain: operator?.domain || record.domain || undefined,
    name: record.name,
  };
}

/** Group only matching ticker AND issuer/security name, never ticker alone.
 * Venue suffixes do not distinguish instruments; CDRs, classes and unrelated
 * issuers do. Without an ISIN, ambiguous or missing names remain separate.
 */
export function groupInstruments(matches: Array<SymbolMatch>) {
  const groups = new Map<
    string,
    { key: string; matches: Array<SymbolMatch> }
  >();
  const seen = new Set<string>();
  for (const match of matches) {
    const code = instrumentExchange(match);
    let name = (match.name ?? '').trim();
    if (code && name.endsWith(`(${code})`))
      name = name.slice(0, -code.length - 2).trim();
    const normalized = name
      .normalize('NFKC')
      .toUpperCase()
      .replace(/[^\p{L}\p{N}]+/gu, ' ')
      .trim();
    const key =
      normalized && normalized !== match.symbol.toUpperCase()
        ? `${match.symbol.toUpperCase()}|${normalized}`
        : `${match.symbol}|${match.conid ?? code}`;
    const identity =
      match.conid != null ? `conid:${match.conid}` : `${key}|${code}`;
    if (seen.has(identity)) continue;
    seen.add(identity);
    let group = groups.get(key);
    if (!group) {
      group = { key, matches: [] };
      groups.set(key, group);
    }
    group.matches.push(match);
  }
  for (const group of groups.values()) {
    // Prefer the established US listing only within this exact instrument.
    const primary = group.matches.findIndex((m) =>
      ['NASDAQ', 'NYSE'].includes(instrumentExchange(m)),
    );
    if (primary > 0) group.matches.unshift(...group.matches.splice(primary, 1));
  }
  return [...groups.values()];
}
